import React, { useState } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassBadge } from '../ui/GlassBadge';
import { apiClient } from '../../services/api';
import { Search, FileText, ExternalLink } from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface InvoiceRow {
    id: string;
    number: string | null;
    created: number;
    amount_due: number;
    currency: string;
    status: string;
    description: string | null;
    invoice_pdf: string | null;
    hosted_invoice_url: string | null;
    period_start: number;
    period_end: number;
}

interface LookupResult {
    user: {
        user_id: string;
        username: string;
        email: string;
        full_name: string | null;
        role: string;
    };
    subscription: {
        customer_id: string;
        subscription_id: string | null;
        plan: string;
        status: string;
        trial_start: string | null;
        trial_end: string | null;
        current_period_start: string | null;
        current_period_end: string | null;
        cancel_at: string | null;
        cancelled_at: string | null;
        created_at: string | null;
    } | null;
    invoices: InvoiceRow[];
    stripe_configured: boolean;
}

const STATUS_COLOURS: Record<string, string> = {
    paid: '#10b981',
    open: '#f59e0b',
    void: '#6b7280',
    uncollectible: '#ef4444',
};

const PLAN_LABELS: Record<string, string> = { trial: 'Trial', growth: 'Growth', pro: 'Pro', enterprise: 'Enterprise' };

const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const fmtUnix = (ts: number) => new Date(ts * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const fmtAmt = (cents: number, currency: string) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);

const BillingLookupPanel: React.FC = () => {
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<LookupResult | null>(null);

    const handleSearch = async () => {
        const q = query.trim();
        if (!q) return;
        setLoading(true);
        setError('');
        setResult(null);

        try {
            const params: Record<string, string> = {};
            // Detect: Stripe customer IDs start with "cus_", otherwise treat as email
            if (q.startsWith('cus_')) {
                params.customer_id = q;
            } else {
                params.email = q;
            }
            const res = await apiClient.get('/billing/admin/lookup', { params });
            setResult(res.data);
        } catch (err: any) {
            setError(getApiError(err, 'No user found matching that query.'));
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    return (
        <div style={{ maxWidth: 860, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Search */}
            <GlassCard padding="lg">
                <h3 style={{ margin: '0 0 0.25rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>
                    Billing Lookup
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 1.25rem' }}>
                    Search by user email address or Stripe customer ID (cus_…) to retrieve full billing history.
                </p>

                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                        <GlassInput
                            label="Email or Customer ID"
                            value={query}
                            onChange={setQuery}
                            placeholder="user@example.com or cus_..."
                        />
                    </div>
                    <GlassButton
                        variant="primary"
                        onClick={handleSearch}
                        disabled={loading || !query.trim()}
                        loading={loading}
                        icon={<Search size={16} />}
                    >
                        Look Up
                    </GlassButton>
                </div>

                {error && (
                    <div style={{ marginTop: '0.75rem', color: 'var(--danger, #ef4444)', fontSize: '0.875rem' }}>
                        {error}
                    </div>
                )}
            </GlassCard>

            {result && (
                <>
                    {/* User & Subscription Info */}
                    <GlassCard padding="lg">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0, color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>
                                {result.user.full_name || result.user.username}
                            </h3>
                            <GlassBadge variant={result.user.role === 'admin' ? 'danger' : 'info'}>
                                {result.user.role}
                            </GlassBadge>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 2rem', fontSize: '0.875rem' }}>
                            {[
                                ['Username', result.user.username],
                                ['Email', result.user.email],
                                ['User ID', result.user.user_id],
                                result.subscription ? ['Customer ID', result.subscription.customer_id] : null,
                                result.subscription ? ['Subscription ID', result.subscription.subscription_id || '—'] : null,
                                result.subscription ? ['Plan', PLAN_LABELS[result.subscription.plan] ?? result.subscription.plan] : null,
                                result.subscription ? ['Status', result.subscription.status] : null,
                                result.subscription ? ['Trial End', fmt(result.subscription.trial_end)] : null,
                                result.subscription ? ['Current Period End', fmt(result.subscription.current_period_end)] : null,
                                result.subscription ? ['Member Since', fmt(result.subscription.created_at)] : null,
                            ].filter(Boolean).map((row) => { const [label, value] = row as string[]; return (
                                <div key={label as string} style={{ display: 'flex', flexDirection: 'column', padding: '0.4rem 0', borderBottom: '1px solid var(--stroke)' }}>
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', marginBottom: 2 }}>{label}</span>
                                    <span style={{ color: 'var(--text)', fontFamily: (label as string).includes('ID') ? 'monospace' : undefined, fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                        {value as string}
                                    </span>
                                </div>
                            ); })}
                        </div>

                        {!result.subscription && (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '1rem' }}>
                                No subscription record found for this user.
                            </p>
                        )}
                    </GlassCard>

                    {/* Invoice History */}
                    <GlassCard padding="lg">
                        <h3 style={{ margin: '0 0 1rem', color: 'var(--text)', fontSize: '1rem', fontWeight: 600 }}>
                            Invoice History
                            <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: '0.85rem', marginLeft: 8 }}>
                                ({result.invoices.length} invoice{result.invoices.length !== 1 ? 's' : ''})
                            </span>
                        </h3>

                        {!result.stripe_configured && (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                Stripe is not configured — invoice retrieval unavailable.
                            </p>
                        )}

                        {result.stripe_configured && result.invoices.length === 0 && (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                No invoices found in Stripe for this customer.
                            </p>
                        )}

                        {result.invoices.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                    <thead>
                                        <tr>
                                            {['Date', 'Invoice #', 'Period', 'Amount', 'Status', 'Actions'].map(h => (
                                                <th key={h} style={{
                                                    textAlign: 'left', padding: '8px 12px',
                                                    borderBottom: '1px solid var(--stroke)',
                                                    color: 'var(--text-secondary)', fontWeight: 600,
                                                    whiteSpace: 'nowrap', background: 'var(--panel-strong)',
                                                }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.invoices.map(inv => (
                                            <tr key={inv.id}>
                                                <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                                                    {fmtUnix(inv.created)}
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                    {inv.number || inv.id.slice(-8)}
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', color: 'var(--text-secondary)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                    {fmtUnix(inv.period_start)} – {fmtUnix(inv.period_end)}
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                    {fmtAmt(inv.amount_due, inv.currency)}
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)' }}>
                                                    <span style={{
                                                        padding: '2px 10px', borderRadius: 999,
                                                        fontSize: '0.78rem', fontWeight: 600,
                                                        background: (STATUS_COLOURS[inv.status] || '#6b7280') + '22',
                                                        color: STATUS_COLOURS[inv.status] || '#6b7280',
                                                        textTransform: 'capitalize',
                                                    }}>
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)' }}>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        {inv.invoice_pdf && (
                                                            <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                                                                style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}
                                                                title="Download PDF">
                                                                <FileText size={15} />
                                                            </a>
                                                        )}
                                                        {inv.hosted_invoice_url && (
                                                            <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                                                                style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}
                                                                title="View in Stripe">
                                                                <ExternalLink size={15} />
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </GlassCard>
                </>
            )}
        </div>
    );
};

export default BillingLookupPanel;
